import React, { useEffect, useRef, useState } from 'react'
import { getSkuData, updateSku } from './SKUService.js'
import { CircularProgress, ListItemText, Avatar, Tooltip, Checkbox } from '@mui/material'
import {ThemeProvider} from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { DataGridPro, LicenseInfo, GridToolbar, useGridApiRef } from '@mui/x-data-grid-pro'
import { filterByValue, stringAvatar } from '../section_sf/Utils.js'
import { SKUSaveButton } from './SKUSaveButton.jsx'
import { SKUDeleteButton } from './SKUDeleteButton.jsx'
import PropTypes from 'prop-types'
import { Config } from 'olympe'
import {_theme} from '../shared/_theme';

export function SKUDataGrid(props) {
    // Add the License for the DataGrid Pro
    const licenseKey = Config.getParameter('uiAppValues.datagrid_license')
    LicenseInfo.setLicenseKey(licenseKey)

    let columns = []
    const pagesNextCursor = useRef({})

    const [selectionModel, setSelectionModel] = useState([])
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(15)
    const [loading, setLoading] = useState(false)
    const [filters, setFilters] = useState({});
    const apiRef = useGridApiRef(null);

    const handleCheckboxClick = (event, property, id) => {
        props.rows[id][property] = event.target.checked
        apiRef.current.setCellFocus(id, 'market')
    }

    // Handler the data refresh on page change or offset change
    const handlePageChange = newPage => {
        // We have the cursor, we can allow the page transition.
        if (newPage === 0 || pagesNextCursor.current[newPage - 1]) {
            setPage(newPage)
        }
    }

    const handleUpdateClick = async (data, operation) => {
        setLoading(true)
        const dataPut = []
        const dataInsert = []
        let result = []
        if (operation === 'PUT') {
            data.forEach((item) => {
                if (item.modifiedBy === "") {
                    dataInsert.push(item)
                } else dataPut.push(item)
            })
            result = await updateSku(data, operation, dataPut, dataInsert)
        } else {
            result = await updateSku(data, operation, [], [])
        }
        await fetchSku(filters)
        setLoading(false)
        if (result.error) {
            props.throwError(null, `An error occurred while updating the record : ${result.message}`)
        }
    }

    const fetchSku = async filters => {
        const nextCursor = pagesNextCursor.current[page - 1]
        if (!nextCursor && page > 0) {
            return
        }

        const newRows = await getSkuData(nextCursor, pageSize, filters)

        if (newRows.error) {
            setLoading(false)
            props.throwError(null, newRows.message)
        } else if (newRows.products === null || newRows.products.length < 1) {
            setLoading(false)
            props.throwError(null, 'Empty response received. Please use different filters.')
        }

        if (newRows.moreRecordsAvailable || newRows.nextCursor === null) {
            pagesNextCursor.current[page] = newRows.nextCursor
        }

        props.setRows(newRows.products)
        setLoading(false)
    }

    useEffect(() => {
        props.filters.subscribe(filters => {

            setPage(0)
            const filtersData = JSON.parse(filters)
            
            const plants = filtersData.plants.length > 0 ? filtersData.plants.map(plant => plant.split(' - ')[1]) : null
            const producingCompanyCode = filtersData.producingCompanyCode.length > 0 ? filtersData.producingCompanyCode : null
            const sellingCompanyCode = filtersData.sellingCompanyCode.length > 0 ? filtersData.sellingCompanyCode : null
            const business = filtersData.business.length > 0 ? filtersData.business : null
            const nestecProductCategory = filtersData.nestecProductCategory.length > 0 ? filtersData.nestecProductCategory : null
            const product = filtersData.product.length > 0 ? filtersData.product : null
            const marketCritical = (filtersData.marketCritical === true || filtersData.marketCritical === false) ? [JSON.stringify(filtersData.marketCritical)] : null 
            const marketShortlist = (filtersData.marketShortlist === true || filtersData.marketShortlist === false) ? [JSON.stringify(filtersData.marketShortlist)] : null
            const exportedProduct = (filtersData.exportedProduct === true || filtersData.exportedProduct === false) ? [JSON.stringify(filtersData.exportedProduct)] : null
            const modifiedBy = filtersData.modifiedBy.length > 0 ? filtersData.modifiedBy : null
            const nnsStatus = filtersData.nnsStatus.length > 0 ? filtersData.nnsStatus : null
            const pl3Category = filtersData.pl3Category.length > 0 ? filtersData.pl3Category : null
            const pl2Business = filtersData.pl2Business.length > 0 ? filtersData.pl2Business : null
            const rangeBrand = filtersData.rangeBrand.length > 0 ? filtersData.rangeBrand : null

            setFilters({
                plants,
                producingCompanyCode,
                sellingCompanyCode,
                business,
                nestecProductCategory,
                product,
                marketCritical,
                marketShortlist,
                exportedProduct,
                modifiedBy,
                nnsStatus,
                pl3Category,
                pl2Business,
                rangeBrand
            })
        })
    }, [])

    useEffect(() => {
        (async () => {
            setLoading(true)
            await fetchSku(filters)
            setLoading(false)
        })()
    }, [page, pageSize, filters])

    if (props.rows && props.rows.length > 0) {
        props.rows.forEach((item, index) => {
            item.id = index
        })

        columns = [
            {
                field: 'market',
                headerName: 'Producing Market',
                minWidth: 150,
                resizable: true,
                valueGetter: params => `${params.row.producingMarket || ''}`,
                renderCell: params => {
                    return <ListItemText primary={params.row.producingMarket} />
                },
            },
            {
                field: 'plant',
                headerName: 'Plant',
                minWidth: 150,
                resizable: true,
                valueGetter: params => `${params.row.plantId || ''} - ${params.row.plantDescription || ''}`,
                renderCell: params => {
                    return <ListItemText
                        primary={params.row.plantId}
                        secondary={params.row.plantDescription}
                    />
                },
            },
            {
                field: 'prodCompanyCode',
                headerName: 'Producing Company Code',
                minWidth: 160,
                resizable: true,
                valueGetter: params => `${params.row.producingCompanyCode || ''} - ${params.row.producingCompanyName || ''}`,
                renderCell: params => {
                    return (
                        <ListItemText
                            primary={params.row.producingCompanyCode}
                        />
                    )
                },
            },
            {
                field: 'sellCompanyCode',
                headerName: 'Selling Company Code',
                minWidth: 160,
                resizable: true,
                valueGetter: params => `${params.row.sellingCompanyCode || ''} - ${params.row.sellingCompanyName || ''}`,
                renderCell: params => {
                    return (
                        <ListItemText
                            primary={params.row.sellingCompanyCode}
                        />
                    )
                },
            },
            {
                field: 'business',
                headerName: 'Business',
                minWidth: 120,
                resizable: true,
                valueGetter: params => `${params.row.business || ''}`,
                renderCell: params => {
                    return (
                        <ListItemText primary={params.row.business} />
                    )
                },
            },
            {
                field: 'pl3Category',
                headerName: 'PL3-Category',
                minWidth: 120,
                resizable: true,
                valueGetter: params => `${params.row.pl3Category || ''}`,
                renderCell: params => {
                    return (
                        <ListItemText primary={params.row.pl3Category} />
                    )
                },
            },
            {
                field: 'rangeBrand',
                headerName: 'Range Brand',
                minWidth: 250,
                resizable: true,
                valueGetter: params => `${params.row.rangeBrand || ''}`,
                renderCell: params => {
                    return (
                        <ListItemText primary={params.row.rangeBrand} />
                    )
                },
            },
            {
                field: 'product',
                headerName: 'Product',
                minWidth: 350,
                height: 'auto',
                resizable: true,
                valueGetter: params => `${parseInt(params.row.product, 10)}`,
                renderCell: params => {
                    return (
                        <ListItemText primary={parseInt(params.row.product, 10)} secondary={params.row.productName} />
                    )
                },
            },
            {
                field: 'nnsStatus',
                headerName: 'NNS Status',
                minWidth: 120,
                height: 'auto',
                resizable: true,
                valueGetter: params => `${params.row.nnsStatus}`,
                renderCell: params => {
                    return (
                        <ListItemText sx={{ fontWeight: 500 }} primary={params.row.nnsStatus} />
                    )
                },
            },
            {
                field: 'marketCritical',
                headerName: 'Market Critical',
                description: 'Reprioritize Finish Goods that are currently define as NNS “Bottom 20%” / “not assigned” to be included in the top 80% of NNS. Important: This will impact your baseline & KPI for Sourcing Flexibility.',
                minWidth: 120,
                height: 'auto',
                resizable: true,
                renderCell: params => {
                    return (
                        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {(params.row.nnsStatus === 'Not Assigned' || params.row.nnsStatus === 'Bottom 20') ?
                                <Checkbox
                                    sx={{
                                        margin: 'auto',
                                        color: 'grey',
                                        '&.Mui-checked': {
                                            color: '#d00033',
                                        },
                                    }}
                                    checked={params.row.marketCritical}
                                    onChange={(event) => handleCheckboxClick(event, 'marketCritical', params.row.id)}
                                />
                                : <></>
                            }
                        </div>
                    )
                },
            },
            {
                field: 'marketShortlist',
                headerName: 'Market Shortlist',
                description: 'Tag Finish Goods to create a shortlist for the market',
                minWidth: 120,
                height: 'auto',
                resizable: true,
                renderCell: params => {
                    return (
                        <Checkbox
                            sx={{
                                margin: 'auto',
                                color: 'grey',
                                '&.Mui-checked': {
                                    color: '#64a80b',
                                },
                            }}
                            checked={params.row.marketShortlist}
                            onChange={(event) => handleCheckboxClick(event, 'marketShortlist', params.row.id)}
                        />
                    )
                },
            },
            {
                field: 'exportedProduct',
                headerName: 'Exported Product',
                description: 'Tag Finish Goods` as exported product. This tag is only enabled for Finish good that are having the selling company code as “not assigned”',
                minWidth: 140,
                height: 'auto',
                resizable: true,
                valueGetter: params => `${params.row.exportedProduct}`,
                renderCell: params => {
                    return (
                        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {params.row.sellingCompanyCode === 'Not Assigned' ?
                                <Checkbox
                                    sx={{
                                        margin: 'auto',
                                        color: 'grey',
                                        '&.Mui-checked': {
                                            color: '#16a4eb',
                                        },
                                    }}
                                    checked={params.row.exportedProduct}
                                    onChange={(event) => handleCheckboxClick(event, 'exportedProduct', params.row.id)}
                                />
                                : <></>
                            }
                        </div>
                    )
                },
            },
            {
                field: 'modifiedBy',
                headerName: 'Modified By',
                width: 140,
                resizable: false,
                valueGetter: params => (params.row['modifiedBy'] ? params.row['modifiedBy']['fullName'] : ''),
                renderCell: params =>
                    (params.row['modifiedBy'] !== "") && (
                        <Tooltip
                            title={`${params.row['modifiedBy']['fullName']} (${params.row['modifiedBy']['email']})`}>
                            <Avatar
                                {...stringAvatar(params.row['modifiedBy']['fullName'])}
                                sx={{ margin: 'auto' }}
                            />
                        </Tooltip>
                    ),
            },
            {
                field: 'lastModified',
                headerName: 'Last Modified',
                minWidth: 140,
                resizable: true,
                renderCell: params => {
                    if (params.row.lastModified) {
                        return (
                            <div className={'component-with-icon'}>
                                {new Date(params.row.lastModified).toLocaleDateString('en-US')} {new Date(params.row.lastModified).toLocaleTimeString('en-US')} 
                            </div>
                        )
                    } else {
                        return (
                            <div className={'component-with-icon'}>
                                -
                            </div>
                        )
                    }
                },
            }
        ]
    }

    const useStyles = makeStyles (
        () => {
            return {
                root: {
                    '& .MuiDataGrid-root': {
                        border: 'none',
                    },
                    '& .MuiPaginationItem-root': {
                        borderRadius: 0,
                    },
                    '& .MuiDataGrid-row': {
                        backgroundColor: '#F9F9F9',
                        borderRadius: '7px',
                        marginBottom: '6px',
                    },
                    '& .MuiDataGrid-cell': {
                        borderBottom: 'none',
                        overflow: 'auto',
                    },
                    '& .wrapHeader .MuiDataGrid-colCellTitle': {
                        overflow: 'hidden',
                        whiteSpace: 'normal',
                    },
                    '& .MuiDataGrid-virtualScrollerContent': {
                        'padding-bottom': '80px !important',
                    },
                },
            }
        },
        { _theme }
    )

    const classes = useStyles()
    
    return <ThemeProvider theme={_theme}>
        <div style={{ display: 'flex', height: '100%' }} className="gridContainer">
            {loading || !props.rows ? (
                <CircularProgress
                    style={{
                        position: 'absolute',
                        top: '48%',
                        left: '48%',
                        width: '4%',
                        height: 'auto',
                    }}
                />
            ) : (
                <div style={{ flexGrow: 1, height: '93%', overflow: 'auto !important' }} className={classes.root}>
                    <div style={{ display: 'flex' }}>
                        <SKUDeleteButton
                            handleUpdateClick={handleUpdateClick}
                            rows={props.rows}
                            selection={selectionModel}
                            throwError={props.throwError}
                        />
                        <SKUSaveButton
                            handleUpdateClick={handleUpdateClick}
                            rows={props.rows}
                            selection={selectionModel}
                            throwError={props.throwError}
                        />
                    </div>
                    <DataGridPro
                        apiRef={apiRef}
                        pagination={true}
                        rowHeight={70}
                        headerHeight={70}
                        page={page}
                        pageSize={pageSize}
                        rowsPerPageOptions={[15, 50, 100]}
                        onPageSizeChange={newPageSize => {
                            setPageSize(newPageSize)
                            setPage(0)
                            pagesNextCursor.current = {}
                        }}
                        onPageChange={handlePageChange}
                        rows={filterByValue(props.rows, props.searchText)}
                        columns={columns}
                        checkboxSelection
                        disableSelectionOnClick={true}
                        density="compact"
                        paginationMode="server"
                        disableColumnMenu={true}
                        disableColumnFilter
                        onSelectionModelChange={newSelection => {
                            setSelectionModel(newSelection)
                        }}
                        getRowId={row => row.recordSequence}
                        rowCount={pageSize * (page + 1) + pageSize}
                        selectionModel={selectionModel}
                        components={{
                            Toolbar: GridToolbar,
                        }}
                        sx={{ marginTop: '2rem' }}
                    />
                </div>
            )}
        </div>
    </ThemeProvider>
}

SKUDataGrid.propTypes = {
    filters: PropTypes.object,
    reload: PropTypes.object,
    searchText: PropTypes.string,
    rows: PropTypes.array,
    setReload: PropTypes.func,
    throwError: PropTypes.func,
    setRows: PropTypes.func,
    setOpenDescription: PropTypes.func,
    setCurrentDescription: PropTypes.func,
    setRowClicked: PropTypes.func,
    handleClickReasonCode: PropTypes.func,
    handleClickExpiry: PropTypes.func,
}
